import { create } from 'zustand';
import type { SlaPolicy } from '@centraoneplus/shared-types';
import { slaService, CreateSlaPolicyDto, UpdateSlaPolicyDto } from '@/services/sla.service';

export type { CreateSlaPolicyDto };

interface SlaState {
  // Data
  policies: SlaPolicy[];
  selectedPolicy: SlaPolicy | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchPolicies: () => Promise<void>;
  fetchPolicy: (id: string) => Promise<SlaPolicy | null>;
  createPolicy: (dto: CreateSlaPolicyDto) => Promise<SlaPolicy | null>;
  updatePolicy: (id: string, dto: UpdateSlaPolicyDto) => Promise<SlaPolicy | null>;
  deletePolicy: (id: string) => Promise<boolean>;
  setSelectedPolicy: (policy: SlaPolicy | null) => void;
  setAsDefault: (id: string) => Promise<boolean>;
  toggleActive: (id: string) => Promise<boolean>;
  reset: () => void;
}

export const useSlaStore = create<SlaState>()((set, get) => ({
  // Initial state
  policies: [],
  selectedPolicy: null,
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  error: null,

  // Fetch all SLA policies
  fetchPolicies: async () => {
    set({ isLoading: true, error: null });
    try {
      const policies = await slaService.getPolicies();
      set({ policies, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch SLA policies',
        isLoading: false,
      });
    }
  },

  // Fetch single policy
  fetchPolicy: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const policy = await slaService.getPolicy(id);
      set({ selectedPolicy: policy, isLoading: false });
      return policy;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch SLA policy',
        isLoading: false,
      });
      return null;
    }
  },

  // Create new policy
  createPolicy: async (dto: CreateSlaPolicyDto) => {
    set({ isSaving: true, error: null });
    try {
      const newPolicy = await slaService.createPolicy(dto);

      // If setting as default, update local state
      let updatedPolicies = get().policies;
      if (dto.isDefault) {
        updatedPolicies = updatedPolicies.map((p) => ({ ...p, isDefault: false }));
      }

      set({
        policies: [...updatedPolicies, newPolicy],
        isSaving: false,
      });

      return newPolicy;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create SLA policy',
        isSaving: false,
      });
      return null;
    }
  },

  // Update existing policy
  updatePolicy: async (id: string, dto: UpdateSlaPolicyDto) => {
    set({ isSaving: true, error: null });
    try {
      const updatedPolicy = await slaService.updatePolicy(id, dto);

      // If setting as default, update other policies
      let policies = get().policies;
      if (dto.isDefault) {
        policies = policies.map((p) => ({ ...p, isDefault: p.id === id }));
      } else {
        policies = policies.map((p) => (p.id === id ? updatedPolicy : p));
      }

      set({
        policies,
        selectedPolicy: get().selectedPolicy?.id === id ? updatedPolicy : get().selectedPolicy,
        isSaving: false,
      });

      return updatedPolicy;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update SLA policy',
        isSaving: false,
      });
      return null;
    }
  },

  // Delete policy
  deletePolicy: async (id: string) => {
    set({ isDeleting: true, error: null });
    try {
      const policy = get().policies.find((p) => p.id === id);
      if (policy?.isDefault) {
        throw new Error('Cannot delete the default SLA policy');
      }

      await slaService.deletePolicy(id);

      set({
        policies: get().policies.filter((p) => p.id !== id),
        selectedPolicy: get().selectedPolicy?.id === id ? null : get().selectedPolicy,
        isDeleting: false,
      });

      return true;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete SLA policy',
        isDeleting: false,
      });
      return false;
    }
  },

  // Set selected policy
  setSelectedPolicy: (policy) => {
    set({ selectedPolicy: policy });
  },

  // Set as default policy
  setAsDefault: async (id: string) => {
    return (await get().updatePolicy(id, { isDefault: true })) !== null;
  },

  // Toggle active status
  toggleActive: async (id: string) => {
    const policy = get().policies.find((p) => p.id === id);
    if (!policy) return false;
    return (await get().updatePolicy(id, { isActive: !policy.isActive })) !== null;
  },

  // Reset state
  reset: () => {
    set({
      policies: [],
      selectedPolicy: null,
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      error: null,
    });
  },
}));
